import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCourseStore } from "../store/courseStore";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";
import SEO from "./SEO";

// Section Components
import BasicInfoSection from "./course-sections/BasicInfoSection";
import MediaSection from "./course-sections/MediaSection";
import CategoriesSection from "./course-sections/CategoriesSection";
import TagsSection from "./course-sections/TagsSection";
import LearningOutcomesSection from "./course-sections/LearningOutcomesSection";
import ModulesSection from "./course-sections/ModulesSection";
import NextSessionsSection from "./course-sections/NextSessionsSection";

export default function AdminCourseEditor() {
  const { id } = useParams();
  const { currentCourse, loading, error, fetchCourse } = useCourseStore();

  React.useEffect(() => {
    if (id) {
      fetchCourse(id, false); // Pass false to indicate this is an ID, not a slug
    }
  }, [id, fetchCourse]);

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SEO
        title={`One80Labs - ${id ? "Edit" : "New"} Course`}
        description="Create or edit a course in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center">
        <Link
          to="/admin/courses"
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Courses
        </Link>
      </div>

      <div className="space-y-8">
        {/* Basic Info Section */}
        <BasicInfoSection courseId={id} initialData={currentCourse} />

        {/* Media Section */}
        <MediaSection courseId={id} initialData={currentCourse} />

        {/* Categories Section */}
        <CategoriesSection courseId={id} initialData={currentCourse} />

        {/* Tags Section */}
        <TagsSection courseId={id} initialData={currentCourse} />

        {/* Learning Outcomes Section */}
        <LearningOutcomesSection
          courseId={id}
          initialData={currentCourse?.course_learning_outcomes || []}
        />

        {/* Modules Section */}
        <ModulesSection
          courseId={id}
          initialData={currentCourse?.course_modules || []}
        />

        {/* Next Sessions Section */}
        <NextSessionsSection courseId={id} initialData={currentCourse} />
      </div>
    </div>
  );
}
